import {
  useGetPastOrdersForReorderQuery,
  type GetPastOrdersForReorderQuery,
  GmOrdersDeliveryTimeFilterCondition,
  GmOrdersSortTerm,
  GmOrdersSortOrder,
} from '@/generated/graphql'

import { useMemo } from 'react'

// orderStatuses.status values from the DB enum: 'sent' / 'confirmed', plus
// the resolver default 'placed' (no row) and 'cancelled' (set by the cancel
// mutation). All non-cancelled past orders are reorderable — the server-side
// deliveryTime filter guarantees they're actually past.
const EXCLUDED_STATUSES = new Set(['cancelled'])

export type PastOrderForReorder = GetPastOrdersForReorderQuery['gmOrders']['data'][number]

export const usePastOrders = ({
  userId,
  vendorId,
}: {
  userId: string | undefined | null
  vendorId: string | undefined | null
}) => {
  // Snap "now" once per mount so the deliveryTime filter doesn't churn the
  // query variables on every render.
  const nowIso = useMemo(() => new Date().toISOString(), [])

  const { data, loading, error } = useGetPastOrdersForReorderQuery({
    variables: {
      filters: {
        userId: userId ?? undefined,
        vendorId: vendorId ?? undefined,
        // Server-side: only orders whose delivery time is in the past.
        // Without this, future-scheduled orders crowd out the limit:20 window
        // and the user can't reach their actual past orders.
        deliveryTime: {
          condition: GmOrdersDeliveryTimeFilterCondition.Lt,
          value: nowIso,
        },
      },
      sortBy: { term: GmOrdersSortTerm.DeliveryTime, order: GmOrdersSortOrder.Desc },
      limit: 20,
      offset: 0,
    },
    skip: !userId || !vendorId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  const orders = useMemo<PastOrderForReorder[]>(() => {
    const all = data?.gmOrders?.data ?? []
    return all.filter((o) => !EXCLUDED_STATUSES.has(o.currentStatus?.name ?? ''))
  }, [data])

  return { orders, loading, error }
}
